import { apiClient, useAuth } from '@isocel/front-common';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AutoComplete, Input, Button } from 'antd';

import { PackagingFlowDirection, StoreUser } from '../../queries/api/types';
import { usePackagingFlow } from '../../context/PackagingFlowContext';
import { useNotification } from '../notification/Notification';
import { validateTransportVoucher } from '../../queries/api/packaging-flow';

interface OrganizationOption {
    value: string;
    id: string;
}

const PackagingRecipient: React.FC = () => {
    const [options, setOptions] = useState<OrganizationOption[]>([]);
    const history = useHistory();
    const { recipientLabel, setRecipientLabel, voucher, setVoucher, setFlowType, setOrganizationId, reset } =
        usePackagingFlow();
    const isButtonDisabled = !(recipientLabel && voucher);
    const { showNotification } = useNotification();
    const { user } = useAuth();

    const storeId = (user as StoreUser)?.store || '';
    const storeDetails = (user as StoreUser)?.storeDetails;

    useEffect(() => {
        reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchOrganizations = async (
        searchText: string,
        setOptionCallback: (options: OrganizationOption[]) => void
    ) => {
        try {
            const response = await apiClient().get('/organizations');
            const organizations = response.data.items.filter(
                (org: { id: string }) => org.id === storeDetails?.organization
            );
            // Filter organizations based on the search text and map them to options
            const filteredOptions = organizations
                .filter((org: { name: string }) => org.name.toLowerCase().includes(searchText.toLowerCase()))
                .sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name))
                .map((org: { name: string; id: string }) => ({ value: org.name, id: org.id }));
            setOptionCallback(filteredOptions);
        } catch (error) {
            console.error('Error fetching organizations:', error);
        }
    };

    const handleVoucherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setVoucher(e.target.value);
    };

    const handleSearch = (value: string) => {
        fetchOrganizations(value, setOptions);
    };

    const handleSelect = (value: string, option: OrganizationOption) => {
        setRecipientLabel(value);
        setOrganizationId(option.id);
    };

    const sendRequest = async () => {
        try {
            // Rule 1: check if this voucher was already received by the parc palette (flowType 3, sender = logged-in store)
            const validateReceivedByParc = await validateTransportVoucher(voucher, 3, storeId);
            if (validateReceivedByParc.voucherFound === true) {
                showNotification(
                    'error',
                    'Erreur',
                    `Le bon de transport ${voucher} a déjà été réceptionné par le parc palette.`
                );
                return;
            }

            // Rule 2: check if this voucher was ever shipped to this store (flowType 0, recipient = logged-in store)
            const validateShippedToStore = await validateTransportVoucher(voucher, 0, storeId);
            if (validateShippedToStore.voucherFound === false) {
                showNotification(
                    'error',
                    'Erreur',
                    `Le bon de transport ${voucher} n'a jamais été expédié vers votre magasin.`
                );
                return;
            }

            const validateVoucher = await validateTransportVoucher(voucher, 2, storeId);
            if (validateVoucher.voucherFound === true) {
                showNotification('error', 'Erreur', 'Un flux d’expédition a déjà été créé pour ce bon de transport');
                return;
            } else {
                setFlowType(PackagingFlowDirection.ShippedToOrganization);
                setVoucher(voucher);
            }
        } catch (error) {
            console.log('error', error);
        }
        history.push(`/recipientTrack/${storeId}`);
    };

    return (
        <div className="reception-info-container">
            <h2 className="reception-info-title">Veuillez saisir les informations du destinataire des emballages</h2>
            <form className="reception-info-form">
                {/* Dropdown Field */}
                <div style={{ marginBottom: '20px', textAlign: 'left' }}>
                    <label htmlFor="recipientLabel">Libellé du destinataire</label>
                    <br />
                    <AutoComplete
                        id="recipientLabel"
                        options={options}
                        className="w-full"
                        notFoundContent="Aucun résultat"
                        onSearch={handleSearch}
                        onSelect={handleSelect}
                        placeholder="Tapez pour rechercher..."
                        onDropdownVisibleChange={(open) => {
                            if (open) {
                                handleSearch('');
                            }
                        }}
                    />
                </div>

                {/* Simple Input Field */}
                <div className="form-field">
                    <label htmlFor="transportNumber">N° de bon de transport</label>
                    <br />
                    <Input
                        id="transportNumber"
                        placeholder="Exemple : 389911024"
                        className="input-field"
                        onChange={handleVoucherChange}
                    />
                </div>

                {/* Submit Button */}
                <div style={{ textAlign: 'center' }}>
                    <Button type="primary" disabled={isButtonDisabled} onClick={sendRequest}>
                        Suivant
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default PackagingRecipient;
